import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchEpicTimeline, postEpicComment } from '../../lib/api/epicTimeline';
import {
  ACTIONS,
  actionCenterFor,
  type ActionDef,
  type ActionKey,
} from '../../../../shared/lib/epicActionCenter';
import type { EpicDetail } from '../../../../shared/types/epics';
import { Button } from '../ui';

// ---------------------------------------------------------------------------
// Action Center — says what Forge is waiting for and offers the exact buttons.
// Every button posts the same slash command a person would type on GitHub.
// ---------------------------------------------------------------------------

const SEVERITY: Record<string, string> = {
  attention: 'border-warning-100 bg-warning-50',
  problem: 'border-danger-100 bg-danger-50',
  info: 'border-slate-200 bg-white',
};

export const ActionCenter: React.FC<{ epic: EpicDetail; epicRef: string }> = ({ epic, epicRef }) => {
  const queryClient = useQueryClient();
  const timeline = useQuery({
    queryKey: ['epic-timeline', epicRef],
    queryFn: () => fetchEpicTimeline(epicRef),
    enabled: epicRef.length > 0,
    staleTime: 10_000,
    refetchInterval: 20_000,
    retry: false,
  });

  const pending = timeline.data?.pending ?? null;
  const blocker = timeline.data?.blocker ?? null;
  const toPrompt = (t: typeof pending) =>
    t ? { title: t.title, htmlUrl: t.htmlUrl, createdAt: t.createdAt, excerpt: t.excerpt } : null;
  const idleMs = Date.now() - new Date(epic.updatedAt).getTime();
  const hasLiveAgent =
    (epic.liveAgents ?? []).some((a) => !a.stale) ||
    (epic.stories ?? []).some((s) => (s.liveAgents ?? []).some((a) => !a.stale));
  const model = actionCenterFor(epic.state, toPrompt(pending), {
    blocker: toPrompt(blocker),
    idleMs: Number.isFinite(idleMs) ? idleMs : 0,
    hasLiveAgent,
  });
  // The message the banner is about: what Forge asked, else what failed.
  const shown = pending ?? (model && model.severity === 'problem' ? blocker : null);

  const [open, setOpen] = useState<ActionKey | null>(null);
  const [input, setInput] = useState('');
  const [done, setDone] = useState<{ label: string; url: string } | null>(null);

  const mutation = useMutation({
    mutationFn: (v: { key: ActionKey; input?: string }) => postEpicComment(epicRef, { action: v.key, input: v.input }),
    onSuccess: (resp, v) => {
      setDone({ label: ACTIONS[v.key].label, url: resp.commentUrl });
      setOpen(null);
      setInput('');
      queryClient.invalidateQueries({ queryKey: ['epic', epicRef] });
      queryClient.invalidateQueries({ queryKey: ['epic-timeline', epicRef] });
    },
  });

  if (!model) return null;

  const run = (key: ActionKey) => {
    const def: ActionDef = ACTIONS[key];
    setDone(null);
    mutation.reset();
    if (def.input || def.confirm) {
      setOpen(key);
      setInput('');
      return;
    }
    mutation.mutate({ key });
  };

  const openDef = open ? ACTIONS[open] : null;
  const canSend = !!openDef && (!openDef.input?.required || input.trim().length > 0);

  return (
    <section
      data-testid="action-center"
      aria-label="Action Center"
      className={`rounded-xl border px-4 py-4 space-y-3 ${SEVERITY[model.severity]}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            {model.severity === 'problem' ? 'Needs attention' : 'Needs you'}
          </div>
          <h2 className="text-base font-semibold text-slate-900" data-testid="action-center-headline">
            {model.headline}
          </h2>
          <p className="text-sm text-slate-700">{model.detail}</p>
        </div>
        {shown && (
          <a
            href={shown.htmlUrl}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary-600 hover:underline whitespace-nowrap"
          >
            Forge's message ↗
          </a>
        )}
      </div>

      {shown && (
        <blockquote
          data-testid="action-center-prompt"
          className="text-sm text-slate-700 bg-white/70 border border-slate-200 rounded-md px-3 py-2 whitespace-pre-wrap"
        >
          <div className="font-medium text-slate-900">{shown.title}</div>
          {shown.excerpt && <div className="mt-1 text-slate-600">{shown.excerpt}</div>}
        </blockquote>
      )}

      {model.checklist && (
        <ul className="text-xs text-slate-600 space-y-0.5" aria-label="Before you approve">
          {model.checklist.map((c) => (
            <li key={c}>☐ {c}</li>
          ))}
        </ul>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {model.actions.map((key, i) => {
          const def = ACTIONS[key];
          return (
            <Button
              key={key}
              size="sm"
              variant={def.tone === 'danger' ? 'danger' : i === 0 && def.tone === 'primary' ? 'primary' : 'secondary'}
              disabled={mutation.isPending}
              onClick={() => run(key)}
              title={def.help}
              data-testid={`ac-${key}`}
            >
              {def.label}
            </Button>
          );
        })}
        {model.linkPr && epic.prUrl && (
          <a
            href={epic.prUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="ac-open-pr"
            className="inline-flex items-center h-7 px-2.5 text-xs font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600"
          >
            Open pull request ↗
          </a>
        )}
      </div>

      {openDef && (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2" data-testid="ac-form">
          <div className="text-sm font-medium text-slate-900">{openDef.label}</div>
          <p className="text-xs text-slate-600">{openDef.help}</p>
          {openDef.confirm && <p className="text-xs text-danger-700">{openDef.confirm}</p>}
          {openDef.input && (
            <label className="block text-xs text-slate-600">
              {openDef.input.label}
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                rows={3}
                placeholder={openDef.input.placeholder}
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm text-slate-900"
                data-testid="ac-input"
              />
            </label>
          )}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant={openDef.tone === 'danger' ? 'danger' : 'primary'}
              disabled={!canSend || mutation.isPending}
              loading={mutation.isPending}
              onClick={() => open && mutation.mutate({ key: open, input })}
              data-testid="ac-send"
            >
              {openDef.confirm && !openDef.input ? `Yes, ${openDef.label.toLowerCase()}` : 'Send'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setOpen(null); setInput(''); }}>
              Cancel
            </Button>
            <span className="text-xs text-slate-500 font-mono">posts {openDef.command || 'a reply'}</span>
          </div>
        </div>
      )}

      {done && (
        <div role="status" className="text-xs text-success-700 bg-success-50 border border-success-100 rounded-md px-2.5 py-1">
          {done.label} sent to GitHub ·{' '}
          <a href={done.url} target="_blank" rel="noreferrer" className="underline">view comment ↗</a>
        </div>
      )}
      {mutation.isError && (
        <div role="alert" className="text-xs text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-2.5 py-1">
          {(mutation.error as Error).message}
        </div>
      )}
    </section>
  );
};
